import { AutoAwesome } from "@mui/icons-material";
import { Alert, Avatar, Button, Chip, DialogContent, DialogTitle, Divider, FormControl, FormLabel, Input, Modal, ModalClose, ModalDialog, ModalOverflow, Snackbar, Textarea, Typography } from "@mui/joy"
import { useEffect, useState } from "react";
import { useAuth } from "../../context/authContext";
import { Report } from '@mui/icons-material';

import LoadingSpinnerComponent from "../LoadingSpinnerComponent";

const replykitNetworking = require('../../Networking/Reply Kit/ReplykitAPI');
const helper = require('../../Helpers/helpers');

export type Reply = {
    id: string;
    category: string;
    body: string;
    language: string;
    created_date: Date;
    is_active: boolean;
}

function ReplyKitPopup({ subscription, app, didSaveNewReply, showReplyKitModal, onClose }: any) {
    const { token } = useAuth();

    const [showAddNewReplyModal, setShowAddNewReplyModal] = useState(false);
    const [error, setError] = useState("");
    const [showError, setShowError] = useState(false);
    const [success, setSuccess] = useState("");
    const [showSuccess, setShowSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [predefinedReplies, setPredefinedReplies] = useState<Reply[]>([]);
    const [currentLanguage, setCurrentLanguage] = useState("");
    const [currentCategory, setCurrentCategory] = useState("");
    const [generatedReply, setGeneratedReply] = useState("");

    useEffect(() => {
        if (subscription.plan === 'AAA' || subscription.plan === 'U') {
            getPredefinedReplies();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getPredefinedReplies = () => {
        setLoading(true);

        replykitNetworking.getRepliesOnKit(app.app.id, token).then((results: any) => {
            setPredefinedReplies(results);
            setLoading(false);
        }).catch((error: Error) => {
            setError("Failed to fetch predefined replies.");
            setShowError(true);
            setLoading(false);
        });
    }

    const createNewReplyTemplate = (event: React.FormEvent<HTMLFormElement>) => {
        setLoading(true);

        event.preventDefault();

        const formData = new FormData(event.currentTarget);
        const formJson = Object.fromEntries(formData.entries());

        replykitNetworking.createReplyOnKit(app.app.id, token, {
            category: formJson.name as string,
            body: formJson.body as string,
            language: formJson.language as string,
            created_date: new Date(),
            isActive: true,
        }).then((results: any) => {
            setSuccess("Reply template created successfully. 🚀");
            setShowSuccess(true);
            setLoading(false);
            getPredefinedReplies();
            didSaveNewReply();
            setShowAddNewReplyModal(false);
        }).catch((error: Error) => {
            setError("Failed to create reply template.");
            setShowError(true);
            setLoading(false);
        });
    }

    const disableReplyTemplate = (replyId: string) => {
        setLoading(true);

        // Find the reply template to disable
        const replyToDisable = predefinedReplies.find(reply => reply.id === replyId);
        if (!replyToDisable) {
            setError("Reply template not found.");
            setShowError(true);
            setLoading(false);

            return;
        }

        // Update the isActive status
        const updatedReply = { ...replyToDisable, is_active: !replyToDisable.is_active };

        replykitNetworking.disableReplyOnKit(updatedReply, token).then((results: any) => {
            setSuccess("Reply template updated successfully. 🚀");
            setShowSuccess(true);
            setLoading(false);
            getPredefinedReplies();
            didSaveNewReply();
        }).catch((error: Error) => {
            setError("Failed to update reply template.");
            setShowError(true);
            setLoading(false);
        });
    }

    const deleteReplyOnKit = (replyId: string) => {
        setLoading(true);

        replykitNetworking.deleteReplyOnKit(replyId, token).then((results: any) => {
            setSuccess("Reply template deleted successfully. 🚀");
            setShowSuccess(true);
            setLoading(false);
            getPredefinedReplies();
            didSaveNewReply();
        }).catch((error: Error) => {
            setError("Failed to delete reply template.");
            setShowError(true);
            setLoading(false);
        });
    }

    const generateWingmanReply = () => {
        setLoading(true);

        replykitNetworking.generateReplyPreviewOnKit(app.app.id, token, currentLanguage, currentCategory).then((results: any) => {
            setSuccess("Wingman generated a reply preview successfully. 🚀");
            setShowSuccess(true);
            setLoading(false);
            setGeneratedReply(results.generatedReply.template);
        }).catch((error: Error) => {
            setError("Failed to generate reply preview with Wingman.");
            setShowError(true);
            setLoading(false);
        });
    }

    return (
        <>
            {/* Added new reply template modal */}
            <>
                {showError &&
                    <Snackbar
                        variant="soft"
                        color="danger"
                        open={showError}
                        onClose={() => setShowError(false)}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        startDecorator={<Report />}
                        endDecorator={
                            <Button
                                onClick={() => setShowError(false)}
                                size="sm"
                                variant="soft"
                                color="danger"
                            >
                                Dismiss
                            </Button>
                        }
                    >
                        {error}
                    </Snackbar>
                }
                {showSuccess &&
                    <Snackbar
                        variant="soft"
                        color="success"
                        open={showSuccess}
                        onClose={() => setShowSuccess(false)}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        startDecorator={<Report />}
                        endDecorator={
                            <Button
                                onClick={() => setShowSuccess(false)}
                                size="sm"
                                variant="soft"
                                color="success"
                            >
                                Dismiss
                            </Button>
                        }
                    >
                        {success}
                    </Snackbar>
                }
                < Modal
                    open={showAddNewReplyModal}
                    onClose={() => setShowAddNewReplyModal(false)}>
                    <ModalOverflow>
                        <ModalDialog
                            color="neutral"
                            layout="center"
                            size="sm"
                            variant="plain"
                            maxWidth={700}>
                            <ModalClose />
                            <DialogTitle>
                                Create New Reply Template
                            </DialogTitle>
                            <Divider />
                            <Typography
                                level="body-sm">
                                Create a new reply template to streamline your responses to user feedback.
                                Customize the content, category, and language to suit your app's needs.
                            </Typography>
                            <Divider />
                            {loading ?
                                <LoadingSpinnerComponent />
                                :
                                <DialogContent>
                                    {/* Form fields for new reply template would go here */}
                                    <form
                                        onSubmit={createNewReplyTemplate}>
                                        <div className="mb-3">
                                            <FormLabel
                                                sx={{ fontSize: '12px', marginBottom: 1 }}>
                                                Name
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    // html input attribute
                                                    name="name"
                                                    placeholder='Name (e.g., bug report, feature request)'
                                                    type="text"
                                                    size='md'
                                                    required
                                                    sx={{
                                                        fontSize: '14px'
                                                    }}
                                                    onChange={(e) => setCurrentCategory(e.target.value)}
                                                />
                                            </FormControl>
                                        </div>
                                        <div className="mb-3">
                                            <FormLabel
                                                sx={{ fontSize: '12px', marginBottom: 1 }}>
                                                Language
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    // html input attribute
                                                    name="language"
                                                    placeholder='Language (e.g., en-en, es-es, fr-fr, pt-pt)'
                                                    type="text"
                                                    size='md'
                                                    required
                                                    sx={{
                                                        fontSize: '14px'
                                                    }}
                                                    onChange={(e) => setCurrentLanguage(e.target.value)}
                                                />
                                            </FormControl>
                                        </div>
                                        <div className="mb-3">
                                            <div className="row my-auto">
                                                <div className="col my-auto">
                                                    <FormLabel
                                                        sx={{ fontSize: '12px', marginBottom: 1 }}>
                                                        Reply
                                                    </FormLabel>
                                                </div>
                                                <div className="col my-auto text-end">
                                                    {currentLanguage !== "" &&
                                                        <Button
                                                            size="sm"
                                                            variant="plain"
                                                            startDecorator={
                                                                <AutoAwesome
                                                                    sx={{
                                                                        width: '14px'
                                                                    }} />}
                                                            sx={{
                                                                float: 'right',
                                                                fontSize: '12px'
                                                            }}
                                                            onClick={() => generateWingmanReply()}>
                                                            Generate with Wingman
                                                        </Button>
                                                    }
                                                </div>
                                            </div>
                                            <Textarea
                                                required
                                                name="body"
                                                defaultValue={generatedReply}
                                                placeholder='Type your reply template here...'
                                                size='md'
                                                minRows={4}
                                                sx={{
                                                    fontSize: '14px'
                                                }}>
                                            </Textarea>
                                        </div>
                                        <Button
                                            type="submit"
                                            sx={{ mt: 2 /* margin top */ }}>
                                            Save Reply Template
                                        </Button>
                                    </form>
                                </DialogContent>
                            }
                        </ModalDialog>
                    </ModalOverflow>
                </Modal >
            </>
            {/* Reply Kit modal */}
            < Modal
                open={showReplyKitModal}
                onClose={() => onClose()}>
                <ModalOverflow>
                    <ModalDialog
                        color="neutral"
                        layout="center"
                        size="sm"
                        variant="plain"
                        maxWidth={700}
                        minWidth={700}>
                        <ModalClose />
                        <DialogTitle>
                            Manage your reply kit
                        </DialogTitle>
                        <Divider />
                        <Typography
                            level="body-sm">
                            Managing your reply kit with Appinion allows you to:
                        </Typography>
                        <ul className='mt-1'>
                            <li>Create and manage reply templates.</li>
                            <li>Customize reply settings to suit your needs.</li>
                        </ul>
                        <Divider />
                        <DialogContent>
                            {predefinedReplies.length === 0 &&
                                <Alert color="warning">
                                    No predefined replies found. Start by adding a new reply template to streamline your responses.
                                </Alert>
                            }
                            {predefinedReplies.length > 0 &&
                                <>
                                    <Typography
                                        level="body-sm"
                                        fontWeight={900}
                                        sx={{
                                            marginBottom: 1
                                        }}>
                                        Saved Predefined Replies:
                                    </Typography>
                                    {predefinedReplies.map((reply: Reply, index: number) => (
                                        <div className="rounded bg-doriean mb-2 p-3">
                                            <div className='row' key={index}>
                                                <div className='col'>
                                                    <div className="flex row gap-2 p-2 mb-2">
                                                        <Chip
                                                            size="sm"
                                                            variant="plain">
                                                            {reply.category}
                                                        </Chip>
                                                        <Chip
                                                            size="sm"
                                                            variant="plain">
                                                            {reply.language}
                                                        </Chip>
                                                        <Chip
                                                            size="sm"
                                                            color={reply.is_active ? "success" : "danger"}>
                                                            {reply.is_active ? "Active" : "Inactive"}
                                                        </Chip>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col-auto">
                                                            <Avatar
                                                                src={app.app.icon}>
                                                                {helper.getInitials(app.app.name)}
                                                            </Avatar>
                                                        </div>
                                                        <div className="col">
                                                            <Typography
                                                                level="body-sm"
                                                                key={index}
                                                                gutterBottom>
                                                                {reply.body}
                                                            </Typography>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className='col-auto my-auto'>
                                                    <Button
                                                        color="danger"
                                                        variant="outlined"
                                                        size="sm"
                                                        sx={{ marginRight: 1 }}
                                                        onClick={() => deleteReplyOnKit(reply.id)}>
                                                        Delete
                                                    </Button>
                                                    <Button
                                                        color="neutral"
                                                        variant="outlined"
                                                        size="sm"
                                                        onClick={() => disableReplyTemplate(reply.id)}>
                                                        {reply.is_active ? "Disable" : "Enable"}
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </>
                            }

                            <Button
                                sx={{ mt: 2 /* margin top */ }}
                                onClick={() => setShowAddNewReplyModal(true)}>
                                Add Predefined Reply
                            </Button>
                        </DialogContent >
                    </ModalDialog>
                </ModalOverflow>
            </Modal >
        </>
    );
}

export default ReplyKitPopup;